import { SUBSCRIBER_STATUSES, USER_STATUS } from '../constants/userStatus';

export type UserStatusValue = (typeof USER_STATUS)[keyof typeof USER_STATUS];

/**
 * Structural shape accepted by the user predicates. Both the resolver `FullUser` type and the
 * generated `UserFullProfileQuery['userFullProfile']` payload satisfy it, so the predicates can
 * be shared by SSR route guards, XState flow guards and `buildFullUserProfile` without coupling
 * to either generated type.
 */
export type UserProfileLike =
    | {
          isLoggedIn?: boolean | null;
          userStatus?: { description?: string | null } | null;
      }
    | null
    | undefined;

export const isLoggedInUser = (user: UserProfileLike): boolean => !!user?.isLoggedIn;

export const isAnonymousUser = (user: UserProfileLike): boolean => !isLoggedInUser(user);

/**
 * Status-only check: true when the profile's `userStatus.description` is one of `statuses`,
 * regardless of `isLoggedIn`. Mirrors the historical semantics of the XState user guards
 * (`isSubscriber`, `isRegistered`, `isExSubscriber`).
 */
export const hasUserStatus = (user: UserProfileLike, statuses: readonly string[]): boolean => {
    const description = user?.userStatus?.description;
    return !!description && statuses.includes(description);
};

/**
 * Strict check: true only when the user is logged in AND has one of `statuses`. Mirrors the
 * historical semantics of the SSR `userRouteGuard`.
 */
export const isLoggedInWithStatus = (
    user: UserProfileLike,
    statuses: readonly UserStatusValue[],
): boolean => isLoggedInUser(user) && hasUserStatus(user, statuses);

export const isSubscriberUser = (user: UserProfileLike): boolean =>
    hasUserStatus(user, SUBSCRIBER_STATUSES);

export const isExSubscriberUser = (user: UserProfileLike): boolean =>
    hasUserStatus(user, [USER_STATUS.EX_SUBSCRIBER]);

export const isRegisteredUser = (user: UserProfileLike): boolean =>
    hasUserStatus(user, [USER_STATUS.REGISTERED]);
